import { IEvent } from 'fabric/fabric-impl';

import { SmartGuideBoundingBox, SmartGuidePosition } from 'js/types';

import ProjectCanvas from './ProjectCanvas';
import { assertIsCameraCanvasElement, assertCanvasElement } from './helpers/canvasElementHelpers';

export default class SmartGuideManager {
  canvas: ProjectCanvas;
  boundingBoxesCache: SmartGuideBoundingBox[] = [];

  constructor(canvas: ProjectCanvas) {
    this.canvas = canvas;

    this.canvas.on('mouse:down', this.cacheBoundingBoxes.bind(this));
    this.canvas.on('mouse:up', this.clearBoundingBoxesCache.bind(this));
    this.canvas.on('object:moving', this.handleSmartGuideDrag.bind(this));
  }

  dispose() {
    this.canvas.off('mouse:down', this.cacheBoundingBoxes.bind(this));
    this.canvas.off('mouse:up', this.clearBoundingBoxesCache.bind(this));
    this.canvas.off('object:moving', this.handleSmartGuideDrag.bind(this));
    this.clearBoundingBoxesCache();
  }

  cacheBoundingBoxes() {
    if (!this.canvas.props.smartGuides) return;

    this.boundingBoxesCache = [];

    this.canvas.getObjects().forEach(el => {
      if (assertIsCameraCanvasElement(el) && !el.cameraPinned) return;

      // Only include elements that are visible
      if (assertCanvasElement(el) && el.visible) {
        // Get the absolute bounding rectangle taking rotation into account
        const boundingRect = el.getBoundingRect(true, true);

        // Calculate center point (accounts for rotation)
        const center = el.getCenterPoint();

        // Store all the data in the cache
        this.boundingBoxesCache.push({
          id: el.id,
          x: boundingRect.left,
          y: boundingRect.top,
          width: boundingRect.width,
          height: boundingRect.height,
          top: boundingRect.top,
          bottom: boundingRect.top + boundingRect.height,
          left: boundingRect.left,
          right: boundingRect.left + boundingRect.width,
          center: {
            x: center.x,
            y: center.y
          }
        });
      }
    });
  }

  clearBoundingBoxesCache() {
    this.boundingBoxesCache = [];

    this.clearTopContext();
  }

  clearTopContext() {
    const ctx = this.canvas.getTopContext();
    if (ctx) {
      // Clear the entire upper canvas
      ctx.clearRect(0, 0, this.canvas.getWidth(), this.canvas.getHeight());

      // Force a rerender to restore controls
      this.canvas.renderAll();
    }
  }

  drawSmartGuideToBox(box: SmartGuideBoundingBox, match: SmartGuidePosition[]) {
    // Get the upper canvas context (controls layer)
    const ctx = this.canvas.getTopContext();

    if (!ctx || this.boundingBoxesCache.length === 0) return;

    const zoom = this.canvas.getZoom();
    const vpt = this.canvas.viewportTransform;

    if (!vpt) return;

    ctx.save();

    ctx.strokeStyle = 'rgba(9.8%, 63.1%, 78%, 0.7)'; // #19A1C7 with 70% opacity
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);

    if (match.includes(SmartGuidePosition.Left)) {
      const leftEdgeX = box.x * zoom + vpt[4];
      ctx.beginPath();
      ctx.moveTo(leftEdgeX, 0);
      ctx.lineTo(leftEdgeX, this.canvas.getHeight());
      ctx.stroke();
    }

    if (match.includes(SmartGuidePosition.Right)) {
      const rightEdgeX = (box.x + box.width) * zoom + vpt[4];
      ctx.beginPath();
      ctx.moveTo(rightEdgeX, 0);
      ctx.lineTo(rightEdgeX, this.canvas.getHeight());
      ctx.stroke();
    }

    if (match.includes(SmartGuidePosition.Top)) {
      const topEdgeY = box.y * zoom + vpt[5];
      ctx.beginPath();
      ctx.moveTo(0, topEdgeY);
      ctx.lineTo(this.canvas.getWidth(), topEdgeY);
      ctx.stroke();
    }

    if (match.includes(SmartGuidePosition.Bottom)) {
      const bottomEdgeY = (box.y + box.height) * zoom + vpt[5];
      ctx.beginPath();
      ctx.moveTo(0, bottomEdgeY);
      ctx.lineTo(this.canvas.getWidth(), bottomEdgeY);
      ctx.stroke();
    }

    if (match.includes(SmartGuidePosition.CenterV)) {
      const centerX = box.center.x * zoom + vpt[4];
      ctx.beginPath();
      ctx.moveTo(centerX, 0);
      ctx.lineTo(centerX, this.canvas.getHeight());
      ctx.stroke();
    }

    if (match.includes(SmartGuidePosition.CenterH)) {
      const centerY = box.center.y * zoom + vpt[5];
      ctx.beginPath();
      ctx.moveTo(0, centerY);
      ctx.lineTo(this.canvas.getWidth(), centerY);
      ctx.stroke();
    }

    ctx.restore();
  }

  handleSmartGuideDrag(event: IEvent) {
    this.clearTopContext();
    // First, make sure we have a target and cached boxes to compare against
    if (!event.target || this.boundingBoxesCache.length === 0 || !this.canvas.props.smartGuides) return;

    const movingObject = event.target;

    // Ensure the object has set its coordinates
    movingObject.setCoords();

    // Get the bounding box of the moving object
    const boundingRect = movingObject.getBoundingRect(true, true);

    // Threshold for snapping in pixels
    const THRESHOLD = 6;

    // For the current moving object, calculate its edges
    const movingLeftEdge = boundingRect.left;
    const movingRightEdge = boundingRect.left + boundingRect.width;
    const movingCenterX = movingLeftEdge + boundingRect.width / 2;
    const movingTopEdge = boundingRect.top;
    const movingBottomEdge = boundingRect.top + boundingRect.height;
    const movingCenterY = boundingRect.top + boundingRect.height / 2;
    const movingWidth = boundingRect.width;
    const movingHeight = boundingRect.height;

    // Get current viewport boundaries
    const viewport = this.canvas.calcViewportBoundaries();
    if (!viewport) return;

    // Convert viewport boundaries to rectangle for easier intersection checking
    const viewportRect = {
      left: viewport.tl.x,
      top: viewport.tl.y,
      right: viewport.br.x,
      bottom: viewport.br.y,
      width: viewport.br.x - viewport.tl.x,
      height: viewport.br.y - viewport.tl.y
    };

    // Skip comparing with self and objects outside of viewport
    const otherBoxes = this.boundingBoxesCache.filter(box => {
      // Skip self
      const notSelf = assertCanvasElement(movingObject) ? box.id !== movingObject.id : true;

      // Check if box is at least partially within the viewport
      const isInViewport =
        box.right >= viewportRect.left &&
        box.left <= viewportRect.right &&
        box.bottom >= viewportRect.top &&
        box.top <= viewportRect.bottom;

      return notSelf && isInViewport;
    });

    for (const box of otherBoxes) {
      // These are the guides we might want to show
      const matchedPositions: SmartGuidePosition[] = [];

      // Get current object center point
      const currentCenter = movingObject.getCenterPoint();
      let newCenterX = currentCenter.x;
      let newCenterY = currentCenter.y;
      let snapApplied = false;

      // HORIZONTALS
      // Moving left edge
      if (Math.abs(movingLeftEdge - box.left) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Left);
        // Calculate new center X based on desired left edge position
        newCenterX = box.left + movingWidth / 2;
        snapApplied = true;
      }
      if (Math.abs(movingLeftEdge - box.right) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Right);
        newCenterX = box.right + movingWidth / 2;
        snapApplied = true;
      }
      if (Math.abs(movingLeftEdge - box.center.x) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.CenterV);
        newCenterX = box.center.x + movingWidth / 2;
        snapApplied = true;
      }

      // Moving right edge
      if (Math.abs(movingRightEdge - box.left) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Left);
        newCenterX = box.left - movingWidth / 2;
        snapApplied = true;
      }
      if (Math.abs(movingRightEdge - box.right) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Right);
        newCenterX = box.right - movingWidth / 2;
        snapApplied = true;
      }
      if (Math.abs(movingRightEdge - box.center.x) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.CenterV);
        newCenterX = box.center.x - movingWidth / 2;
        snapApplied = true;
      }

      // Moving horizontal center
      if (Math.abs(movingCenterX - box.left) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Left);
        newCenterX = box.left;
        snapApplied = true;
      }
      if (Math.abs(movingCenterX - box.right) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Right);
        newCenterX = box.right;
        snapApplied = true;
      }
      if (Math.abs(movingCenterX - box.center.x) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.CenterV);
        newCenterX = box.center.x;
        snapApplied = true;
      }

      // VERTICALS
      // Moving top edge
      if (Math.abs(movingTopEdge - box.top) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Top);
        newCenterY = box.top + movingHeight / 2;
        snapApplied = true;
      }
      if (Math.abs(movingTopEdge - box.bottom) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Bottom);
        newCenterY = box.bottom + movingHeight / 2;
        snapApplied = true;
      }
      if (Math.abs(movingTopEdge - box.center.y) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.CenterH);
        newCenterY = box.center.y + movingHeight / 2;
        snapApplied = true;
      }

      // Moving bottom edge
      if (Math.abs(movingBottomEdge - box.top) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Top);
        newCenterY = box.top - movingHeight / 2;
        snapApplied = true;
      }
      if (Math.abs(movingBottomEdge - box.bottom) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Bottom);
        newCenterY = box.bottom - movingHeight / 2;
        snapApplied = true;
      }
      if (Math.abs(movingBottomEdge - box.center.y) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.CenterH);
        newCenterY = box.center.y - movingHeight / 2;
        snapApplied = true;
      }

      // Moving vertical center
      if (Math.abs(movingCenterY - box.top) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Top);
        newCenterY = box.top;
        snapApplied = true;
      }
      if (Math.abs(movingCenterY - box.bottom) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.Bottom);
        newCenterY = box.bottom;
        snapApplied = true;
      }
      if (Math.abs(movingCenterY - box.center.y) < THRESHOLD) {
        matchedPositions.push(SmartGuidePosition.CenterH);
        newCenterY = box.center.y;
        snapApplied = true;
      }

      // Apply snapped position if needed
      if (snapApplied) {
        // Use setPositionByOrigin to handle rotated objects correctly
        movingObject.setPositionByOrigin(new fabric.Point(newCenterX, newCenterY), 'center', 'center');
        movingObject.setCoords();
      }

      if (matchedPositions.length) {
        movingObject.setCoords();
        this.drawSmartGuideToBox(box, matchedPositions);
      }
    }
  }
}
